import {
  buildOutcomeGreenRateLabel,
  getDefaultOutcomeResultLabel,
  getOutcomeNoun,
  getOutcomePluralNoun,
  greenRedYellowResultTypeCategory,
  isFunnelOutcomeDefinition,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { type FC, useState } from "react";
import { OutcomeTemplateChooserDialog } from "./outcomeTemplateChooserDialog.js";

interface OutcomeLabelsEditorSlideoverDialogProps {
  outcome: OutcomeDefinition;
  onSave: (outcome: OutcomeDefinition) => void;
}

/**
 * A single home for the transcript template, its value names, and the
 * terminology used in reporting. The draft includes the full outcome so a
 * template change and subsequent label edits are saved together.
 */
export const OutcomeLabelsEditorSlideoverDialog: FC<
  OutcomeLabelsEditorSlideoverDialogProps
> = ({ outcome, onSave }) => {
  const [draftOutcome, setDraftOutcome] = useState<OutcomeDefinition>(outcome);
  const [isTemplateChooserOpen, setIsTemplateChooserOpen] = useState(false);

  const canChooseTemplate =
    !isFunnelOutcomeDefinition(draftOutcome) &&
    !isRevenueOutcomeDefinition(draftOutcome);

  const baseDefaultsOutcome: OutcomeDefinition = {
    ...draftOutcome,
    singular_noun: undefined,
    plural_noun: undefined,
    rate_label: undefined,
  };
  const previewOutcome: OutcomeDefinition = {
    ...baseDefaultsOutcome,
    singular_noun: draftOutcome.singular_noun?.trim() || undefined,
  };
  const singularNounPlaceholder = getOutcomeNoun(baseDefaultsOutcome);
  const pluralNounPlaceholder = getOutcomePluralNoun(previewOutcome);
  const rateLabelPlaceholder = buildOutcomeGreenRateLabel(previewOutcome);

  const handleSave = (close: () => void): void => {
    const results = draftOutcome.results.map((result) => ({
      ...result,
      label:
        result.label.trim() ||
        getDefaultOutcomeResultLabel(draftOutcome.id, result.value) ||
        getDefaultOutcomeResultLabel(outcome.id, result.value) ||
        result.label,
    }));

    onSave({
      ...draftOutcome,
      results,
      singular_noun: draftOutcome.singular_noun?.trim() || undefined,
      plural_noun: draftOutcome.plural_noun?.trim() || undefined,
      rate_label: draftOutcome.rate_label?.trim() || undefined,
    });
    close();
  };

  return (
    <SlideoverDialog label="Edit outcome">
      {({ close }) => (
        <>
          <SlideoverDialogTitleHeader isLoading={false}>
            Edit outcome
          </SlideoverDialogTitleHeader>

          <SlideoverDialogContent>
            <div className="space-y-8 p-6">
              {canChooseTemplate ? (
                <section className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Template
                    </h3>
                    <p className="mt-1 text-sm leading-5 text-muted-foreground">
                      The template provides the starting values and
                      instructions. You can customize the value names below.
                    </p>
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-md border border-border-light px-3 py-2.5">
                    <span className="min-w-0 truncate text-sm font-medium text-foreground">
                      {draftOutcome.name}
                    </span>
                    <Button
                      variant={ButtonVariant.Secondary}
                      size={Size.MEDIUM}
                      onClick={() => setIsTemplateChooserOpen(true)}
                    >
                      Change template
                    </Button>
                  </div>
                </section>
              ) : null}

              <section className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Values
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Rename the values used to classify each customer
                    interaction.
                  </p>
                </div>

                <div className="space-y-4">
                  {draftOutcome.results.map((result) => {
                    const defaultLabel =
                      getDefaultOutcomeResultLabel(
                        draftOutcome.id,
                        result.value,
                      ) ?? result.label;
                    const isNotApplicable =
                      result.category === greenRedYellowResultTypeCategory.gray;

                    return (
                      <Input
                        key={result.value}
                        label={defaultLabel}
                        value={result.label}
                        placeholder={defaultLabel}
                        isDisabled={isNotApplicable}
                        onChange={(event) =>
                          setDraftOutcome((current) => ({
                            ...current,
                            results: current.results.map((currentResult) =>
                              currentResult.value === result.value
                                ? {
                                    ...currentResult,
                                    label: event.target.value,
                                  }
                                : currentResult,
                            ),
                          }))
                        }
                        maxLength={100}
                      />
                    );
                  })}
                </div>
              </section>

              <section className="space-y-4 border-t border-border-light pt-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Reporting terminology
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground">
                    Customize how the positive result appears in dashboards and
                    reports.
                  </p>
                </div>

                <div className="space-y-4">
                  <Input
                    label="Singular noun"
                    value={draftOutcome.singular_noun ?? ""}
                    placeholder={singularNounPlaceholder}
                    description='For example, "win".'
                    onChange={(event) =>
                      setDraftOutcome((current) => ({
                        ...current,
                        singular_noun: event.target.value || undefined,
                      }))
                    }
                    maxLength={100}
                  />
                  <Input
                    label="Plural noun"
                    value={draftOutcome.plural_noun ?? ""}
                    placeholder={pluralNounPlaceholder}
                    description='For example, "wins".'
                    onChange={(event) =>
                      setDraftOutcome((current) => ({
                        ...current,
                        plural_noun: event.target.value || undefined,
                      }))
                    }
                    maxLength={100}
                  />
                  <Input
                    label="Conversion metric label"
                    value={draftOutcome.rate_label ?? ""}
                    placeholder={rateLabelPlaceholder}
                    description='For example, "Win rate".'
                    onChange={(event) =>
                      setDraftOutcome((current) => ({
                        ...current,
                        rate_label: event.target.value || undefined,
                      }))
                    }
                    maxLength={100}
                  />
                </div>
              </section>
            </div>
          </SlideoverDialogContent>

          <SlideoverDialogButtons>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.MEDIUM}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              size={Size.MEDIUM}
              onClick={() => handleSave(close)}
            >
              Save
            </Button>
          </SlideoverDialogButtons>

          <OutcomeTemplateChooserDialog
            isOpen={isTemplateChooserOpen}
            isReplacingOutcome
            initialTemplateId={draftOutcome.id}
            onOpenChange={setIsTemplateChooserOpen}
            onChoose={setDraftOutcome}
          />
        </>
      )}
    </SlideoverDialog>
  );
};
